import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS, getImageUrl } from '../../utils/GlobalAPI';
import { sendPasswordCreationConfirmation } from '../../utils/emailService';
import { 
  FiEdit, 
  FiLogOut, 
  FiCheck, 
  FiUpload, 
  FiInstagram,
  FiFacebook,
  FiTwitter,
  FiDollarSign,
  FiPercent,
  FiEye
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Validation schema
const profileSchema = yup.object().shape({
  influencerName: yup.string().required('Display name is required'),
  bio: yup.string().max(500, 'Bio must be less than 500 characters'),
  category: yup.string().required('Category is required'),
  instaSubs: yup.string(),
  facebook: yup.string(),
  twitter: yup.string(),
  clients: yup.string(),
  awards: yup.string(),
  price: yup.number().min(0, 'Price must be positive').typeError('Price must be a number'),
  engagementPercentage: yup.number()
    .min(0, 'Engagement must be between 0-100')
    .max(100, 'Engagement must be between 0-100')
    .typeError('Engagement must be a number'),
  averageViews: yup.number().min(0, 'Views must be positive').typeError('Views must be a number'),
  phone: yup.string().required('Phone number is required').matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit phone number')
});

const InfluencerProfile = ({ isModal = false }) => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [influencerData, setInfluencerData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [loginFormData, setLoginFormData] = useState({
    email: '',
    password: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [needsPasswordCreation, setNeedsPasswordCreation] = useState(false);

  const { 
    register, 
    handleSubmit, 
    reset,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(profileSchema),
    mode: 'onChange'
  });

  // Initialize form with influencer data
  useEffect(() => {
    if (influencerData) {
      reset({
        influencerName: influencerData.influencerName || '',
        bio: influencerData.bio || '',
        category: influencerData.category || 'Other',
        instaSubs: influencerData.instaSubs || '',
        facebook: influencerData.facebook || '',
        twitter: influencerData.twitter || '',
        clients: influencerData.clients?.join(', ') || '',
        awards: influencerData.awards?.join(', ') || '',
        price: influencerData.price || 0,
        engagementPercentage: influencerData.engagementPercentage || 0,
        averageViews: influencerData.averageViews || 0,
        phone: influencerData.phone ? influencerData.phone.replace('+91 ', '') : ''
      });
    }
  }, [influencerData, reset]);

  // Check authentication status
  useEffect(() => {
    const token = localStorage.getItem('influencerToken');
    if (token) {
      fetchProfile(token);
    }
  }, []);

  const fetchProfile = useCallback(async (token) => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.INFLUENCER_PROFILE, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInfluencerData(response.data);
      setIsLoggedIn(true);
      setPreviewImage(getImageUrl(response.data.influencerImage));
    } catch (err) {
      console.error('Error fetching profile:', err);
      handleLogout();
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(API_ENDPOINTS.INFLUENCER_LOGIN, {
        email: loginFormData.email,
        password: loginFormData.password,
        newPassword: loginFormData.newPassword,
        confirmNewPassword: loginFormData.confirmNewPassword
      });

      // Check if user needs to create password
      if (response.data.needsPasswordCreation) {
        setNeedsPasswordCreation(true);
        setError('');
        return;
      }

      // Check if password was just created (user had no password before and now has token)
      const passwordJustCreated = loginFormData.newPassword && 
                                   loginFormData.confirmNewPassword && 
                                   response.data.token &&
                                   response.data.influencer;

      localStorage.setItem('influencerToken', response.data.token);
      await fetchProfile(response.data.token);

      // Send password creation confirmation email if password was just created
      if (passwordJustCreated && response.data.influencer) {
        try {
          // Get full influencer data for email
          const influencerDataForEmail = {
            name: response.data.influencer.name || loginFormData.email,
            email: response.data.influencer.email || loginFormData.email,
            instagram: response.data.influencer.instagram || '',
            influencerType: response.data.influencer.influencerType || 'Normal',
            influencerId: response.data.influencer.influencerId || response.data.influencer._id || ''
          };
          await sendPasswordCreationConfirmation(influencerDataForEmail);
          console.log('✅ Password creation confirmation email sent');
        } catch (emailError) {
          console.error('❌ Failed to send password creation email:', emailError);
          // Don't fail login if email fails
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('influencerToken');
    setIsLoggedIn(false);
    setInfluencerData(null);
    setLoginFormData({
      email: '',
      password: '',
      newPassword: '',
      confirmNewPassword: ''
    });
    setNeedsPasswordCreation(false);
  };

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 2 * 1024 * 1024) { // 2MB limit
      setProfileImage(file);
      setPreviewImage(URL.createObjectURL(file));
    } else if (file) {
      setError('Image size must be less than 2MB');
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');

      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Add +91 prefix to phone number
          if (key === 'phone' && value) {
            formData.append(key, `+91 ${value}`);
          } else {
            formData.append(key, value);
          }
        }
      });

      if (profileImage) {
        formData.append('influencerImage', profileImage);
      }

      const token = localStorage.getItem('influencerToken');
      const response = await axios.patch(
        API_ENDPOINTS.INFLUENCER_PROFILE,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );

      setInfluencerData(response.data);
      setEditMode(false);
      setProfileImage(null);
      setPreviewImage(getImageUrl(response.data.influencerImage));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const renderStatCard = (icon, title, value, unit = '') => (
    <div className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center mb-2">
        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 text-blue-600 mr-2.5">
          {icon}
        </div>
        <p className="text-xs text-gray-500 font-medium">{title}</p>
      </div>
      <p className="text-xl font-bold text-gray-800">
        {value || 'N/A'}{unit}
      </p>
    </div>
  );

  if (loading && !influencerData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-orange-500/5 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>

        {/* Floating Dots */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0.2, 0.8, 0.2],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
              <span className="text-2xl font-bold text-white">D</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Creator Login
            </h2>
            <p className="text-white/80 text-sm">
              Sign in to your creator account
            </p>
          </motion.div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg py-8 px-6 shadow-2xl rounded-2xl border border-white/20"
          >
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mb-6 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 p-4 rounded-xl"
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="ml-3 text-sm text-red-700 font-medium">{error}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <form className="space-y-6" onSubmit={handleLogin}>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={loginFormData.email}
                    onChange={handleLoginInputChange}
                    className="w-full px-4 py-3 text-sm border-2 border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/10 backdrop-blur-sm text-white placeholder-white/60"
                    placeholder="Enter your email"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                </div>
              </div>

              {!needsPasswordCreation ? (
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-white mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={loginFormData.password}
                      onChange={handleLoginInputChange}
                      className="w-full px-4 py-3 text-sm border-2 border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/10 backdrop-blur-sm text-white placeholder-white/60"
                      placeholder="Enter your password"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-4">
                    <div className="flex items-center mb-2">
                      <svg className="h-5 w-5 text-blue-300 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-blue-200 text-sm font-medium">Create Your Password</p>
                    </div>
                    <p className="text-blue-100 text-xs">Welcome! Please create a password to secure your account.</p>
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-semibold text-white mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={loginFormData.newPassword}
                        onChange={handleLoginInputChange}
                        className="w-full px-4 py-3 text-sm border-2 border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/10 backdrop-blur-sm text-white placeholder-white/60"
                        placeholder="Create a new password (min 6 characters)"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmNewPassword" className="block text-sm font-semibold text-white mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmNewPassword"
                        name="confirmNewPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={loginFormData.confirmNewPassword}
                        onChange={handleLoginInputChange}
                        className="w-full px-4 py-3 text-sm border-2 border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/10 backdrop-blur-sm text-white placeholder-white/60"
                        placeholder="Confirm your new password"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-2">
                <motion.button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-xl shadow-lg text-base font-semibold text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 ${
                    loading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl transform hover:-translate-y-0.5'
                  }`}
                  whileHover={!loading ? { scale: 1.02 } : {}}
                  whileTap={!loading ? { scale: 0.98 } : {}}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing in...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <span>{needsPasswordCreation ? 'Create Password & Sign In' : 'Sign In'}</span>
                      <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isModal ? '' : 'min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8'}`}>
      <div className={`${isModal ? '' : 'max-w-7xl mx-auto'}`}>
        {/* Header - Only show if not in modal */}
        {!isModal && (
          <div className="bg-white shadow rounded-xl overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-4 sm:px-8 sm:py-6 flex flex-col sm:flex-row justify-between items-center">
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-0">
                Influencer Profile
              </h1>
              <div className="flex space-x-2 sm:space-x-3 w-full sm:w-auto">
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="flex items-center px-3 py-2 sm:px-4 sm:py-2 bg-white/20 text-white rounded-lg sm:rounded-xl hover:bg-white/30 transition-all text-sm sm:text-base"
                >
                  <FiEdit className="mr-1 sm:mr-2" />
                  Edit
                </button>
              ) : (
                <button
                  onClick={() => {
                    setEditMode(false);
                    setProfileImage(null);
                    setPreviewImage(getImageUrl(influencerData?.influencerImage));
                  }}
                  className="flex items-center px-3 py-2 sm:px-4 sm:py-2 bg-white/20 text-white rounded-lg sm:rounded-xl hover:bg-white/30 transition-all text-sm sm:text-base"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 sm:px-4 sm:py-2 bg-white/20 text-white rounded-lg sm:rounded-xl hover:bg-white/30 transition-all text-sm sm:text-base"
              >
                <FiLogOut className="mr-1 sm:mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
        )}
        
        {/* Action Buttons - Show in modal mode */}
        {isModal && (
          <div className="flex justify-end space-x-3 mb-6">
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all text-sm font-medium shadow-md"
              >
                <FiEdit className="mr-2" size={16} />
                Edit Profile
              </button>
            ) : (
              <button
                onClick={() => {
                  setEditMode(false);
                  setProfileImage(null);
                  setPreviewImage(getImageUrl(influencerData?.influencerImage));
                }}
                className="flex items-center px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all text-sm font-medium"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm font-medium shadow-md"
            >
              <FiLogOut className="mr-2" size={16} />
              Logout
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Main Content */}
        {!editMode ? (
          // View Mode
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Profile Card (Left Column) */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-lg border border-gray-100 sticky top-6">
                <div className="flex flex-col items-center">
                  <div className="relative w-36 h-36 rounded-full overflow-hidden border-4 border-white shadow-xl mb-5 ring-4 ring-blue-100">
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-blue-200 to-cyan-200 flex items-center justify-center">
                        <span className="text-3xl sm:text-4xl font-bold text-white">
                          {influencerData?.name?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 text-center mb-2">
                    {influencerData?.influencerName || influencerData?.name}
                  </h2>
                  <span className="inline-block mt-1 px-4 py-1.5 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 rounded-full text-xs font-semibold border border-blue-200">
                    {influencerData?.category || 'Other'}
                  </span>

                  {/* Stats */}
                  <div className="mt-6 w-full space-y-3 bg-white/60 rounded-xl p-4 border border-gray-100">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500 font-medium">Followers</span>
                      <span className="font-bold text-gray-800">
                        {influencerData?.instaSubs || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500 font-medium">Price</span>
                      <span className="font-bold text-gray-800">
                        {influencerData?.price ? `₹${influencerData.price}` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500 font-medium">Status</span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                        influencerData?.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : influencerData?.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {influencerData?.status?.toUpperCase() || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="mt-5 flex flex-col gap-3 w-full max-w-xs">
                    {influencerData?.instagram && (
                      <a
                        href={`https://instagram.com/${influencerData.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-pink-500 via-purple-500 to-pink-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-xs font-semibold"
                      >
                        <FiInstagram className="mr-2" size={16} />
                        {influencerData.instagram}
                      </a>
                    )}
                    {influencerData?.facebook && (
                      <a
                        href={influencerData.facebook.includes('http') ? influencerData.facebook : `https://${influencerData.facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-xs font-semibold"
                      >
                        <FiFacebook className="mr-2" size={16} />
                        Facebook
                      </a>
                    )}
                    {influencerData?.twitter && (
                      <a
                        href={influencerData.twitter.includes('http') ? influencerData.twitter : `https://${influencerData.twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-sky-400 to-sky-500 text-white rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-xs font-semibold"
                      >
                        <FiTwitter className="mr-2" size={16} />
                        X (Twitter)
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Details (Right Column) */}
            <div className="lg:col-span-3 space-y-5">
              {/* About Section */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                  <span className="w-1 h-5 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full mr-2"></span>
                  About
                </h3>
                <p className="text-gray-600 leading-relaxed text-xs">
                  {influencerData?.bio || 'No bio provided'}
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Clients */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                  <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                    <span className="w-1 h-5 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full mr-2"></span>
                    Clients
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {(influencerData?.clients || []).length > 0 ? (
                      influencerData.clients.map((client, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg text-xs font-medium shadow-sm"
                        >
                          {client}
                        </span>
                      ))
                    ) : (
                      <p className="text-gray-400 text-xs">No clients listed</p>
                    )}
                  </div>
                </div>

                {/* Awards */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                  <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                    <span className="w-1 h-5 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full mr-2"></span>
                    Awards
                  </h3>
                  <ul className="space-y-2.5">
                    {(influencerData?.awards || []).length > 0 ? (
                      influencerData.awards.map((award, index) => (
                        <li key={index} className="flex items-start">
                          <span className="inline-flex items-center justify-center w-5 h-5 bg-gradient-to-br from-blue-100 to-cyan-100 text-blue-600 rounded-full mr-2.5 mt-0.5 shadow-sm">
                            <FiCheck size={11} />
                          </span>
                          <span className="text-xs text-gray-700 leading-relaxed">{award}</span>
                        </li>
                      ))
                    ) : (
                      <p className="text-gray-400 text-xs">No awards listed</p>
                    )}
                  </ul>
                </div>
              </div>

              {/* Engagement Metrics */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-1 h-5 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full mr-2"></span>
                  Engagement Metrics
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {renderStatCard(
                    <FiDollarSign size={16} />,
                    'Price',
                    influencerData?.price,
                    ''
                  )}
                  {renderStatCard(
                    <FiPercent size={16} />,
                    'Engagement Rate',
                    influencerData?.engagementPercentage,
                    '%'
                  )}
                  {renderStatCard(
                    <FiEye size={16} />,
                    'Avg. Views',
                    influencerData?.averageViews,
                    ''
                  )}
                </div>
              </div>

              {/* Account Info */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-1 h-5 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full mr-2"></span>
                  Account Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 font-medium">Email</p>
                    <p className="font-semibold text-xs text-gray-800">
                      {influencerData?.email || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 font-medium">Phone</p>
                    <p className="font-semibold text-xs text-gray-800">
                      {influencerData?.phone || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 font-medium">Influencer ID</p>
                    <p className="font-semibold text-xs text-gray-800">
                      {influencerData?.influencerId || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 font-medium">Member Since</p>
                    <p className="font-semibold text-xs text-gray-800">
                      {influencerData?.createdAt 
                        ? new Date(influencerData.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Edit Mode
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Profile Image Upload */}
              <div className="lg:col-span-1">
                <div className="bg-white p-6 rounded-xl shadow-md sticky top-6">
                  <div className="flex flex-col items-center">
                    <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 border-white shadow-lg mb-4 sm:mb-6">
                      {previewImage ? (
                        <img
                          src={previewImage}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : influencerData?.influencerImage ? (
                        <img
                          src={getImageUrl(influencerData.influencerImage)}
                          alt="Current"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-r from-blue-200 to-cyan-200 flex items-center justify-center">
                          <span className="text-3xl sm:text-4xl font-bold text-white">
                            {influencerData?.name?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      id="profileImage"
                      name="profileImage"
                      onChange={handleImageChange}
                      accept="image/*"
                      className="hidden"
                    />
                    <label
                      htmlFor="profileImage"
                      className="flex items-center px-3 py-2 sm:px-4 sm:py-2 bg-white text-blue-600 rounded-lg sm:rounded-xl shadow hover:bg-gray-50 cursor-pointer transition-all text-sm sm:text-base"
                    >
                      <FiUpload className="mr-1 sm:mr-2" />
                      Change Photo
                    </label>
                    <p className="mt-2 text-xs text-gray-500">JPG, PNG up to 2MB</p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="lg:col-span-3 space-y-6">
                {/* Basic Info */}
                <div className="bg-white p-6 rounded-xl shadow">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label htmlFor="influencerName" className="block text-sm font-medium text-gray-700 mb-1">
                        Display Name*
                      </label>
                      <input
                        id="influencerName"
                        {...register('influencerName')}
                        className={`w-full px-4 py-3 border ${
                          errors.influencerName ? 'border-red-300' : 'border-gray-300'
                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base`}
                      />
                      {errors.influencerName && (
                        <p className="mt-1 text-xs text-red-500">{errors.influencerName.message}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number*
                      </label>
                      <div className="flex rounded-xl shadow-sm">
                        <span className="inline-flex items-center px-4 rounded-l-xl border-2 border-r-0 border-gray-300 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-600 text-sm font-medium">
                          +91
                        </span>
                        <input
                          id="phone"
                          {...register('phone')}
                          type="tel"
                          className={`flex-1 min-w-0 block w-full px-4 py-3 rounded-none rounded-r-xl border-2 ${
                            errors.phone ? 'border-red-300' : 'border-gray-300'
                          } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base`}
                          placeholder="9876543210"
                          maxLength="10"
                        />
                      </div>
                      {errors.phone && (
                        <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                        Category*
                      </label>
                      <select
                        id="category"
                        {...register('category')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      >
                        <option value="Food">Food</option>
                        <option value="Travel">Travel</option>
                        <option value="Finance">Finance</option>
                        <option value="Fitness">Fitness</option>
                        <option value="Fashion">Fashion</option>
                        <option value="Gaming">Gaming</option>
                        <option value="Tech">Tech</option>
                        <option value="Education">Education</option>
                        <option value="Entertainment">Entertainment</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Bio */}
                <div className="bg-white p-6 rounded-xl shadow">
                  <label htmlFor="bio" className="block text-lg font-semibold text-gray-800 mb-3">
                    Bio
                  </label>
                  <textarea
                    id="bio"
                    {...register('bio')}
                    rows={4}
                    className={`w-full px-4 py-3 border ${
                      errors.bio ? 'border-red-300' : 'border-gray-300'
                    } rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base`}
                    placeholder="Tell brands about yourself, your content style, and what makes you unique..."
                  />
                  {errors.bio && (
                    <p className="mt-1 text-xs text-red-500">{errors.bio.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Max 500 characters
                  </p>
                </div>

                {/* Stats */}
                <div className="bg-white p-6 rounded-xl shadow">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Statistics
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label htmlFor="instaSubs" className="block text-sm font-medium text-gray-700 mb-1">
                        Instagram Subscribers
                      </label>
                      <input
                        id="instaSubs"
                        {...register('instaSubs')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        placeholder="e.g. 50K"
                      />
                    </div>
                    <div>
                      <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-1">
                        Facebook URL
                      </label>
                      <input
                        id="facebook"
                        {...register('facebook')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        placeholder="e.g. facebook.com/username"
                      />
                    </div>
                    <div>
                      <label htmlFor="twitter" className="block text-sm font-medium text-gray-700 mb-1">
                        X (Twitter) Handle
                      </label>
                      <input
                        id="twitter"
                        {...register('twitter')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        placeholder="e.g. x.com/username"
                      />
                    </div>
                  </div>
                </div>

                {/* Engagement Metrics */}
                <div className="bg-white p-6 rounded-xl shadow">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Engagement Metrics
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                    <div>
                      <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                        Price ($)
                      </label>
                      <input
                        id="price"
                        type="number"
                        {...register('price')}
                        className={`w-full px-4 py-3 border ${
                          errors.price ? 'border-red-300' : 'border-gray-300'
                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base`}
                        placeholder="e.g. 500"
                      />
                      {errors.price && (
                        <p className="mt-1 text-xs text-red-500">{errors.price.message}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="engagementPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                        Engagement Rate (%)
                      </label>
                      <input
                        id="engagementPercentage"
                        type="number"
                        {...register('engagementPercentage')}
                        className={`w-full px-4 py-3 border ${
                          errors.engagementPercentage ? 'border-red-300' : 'border-gray-300'
                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base`}
                        placeholder="e.g. 3.5"
                      />
                      {errors.engagementPercentage && (
                        <p className="mt-1 text-xs text-red-500">{errors.engagementPercentage.message}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="averageViews" className="block text-sm font-medium text-gray-700 mb-1">
                        Average Views
                      </label>
                      <input
                        id="averageViews"
                        type="number"
                        {...register('averageViews')}
                        className={`w-full px-4 py-3 border ${
                          errors.averageViews ? 'border-red-300' : 'border-gray-300'
                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base`}
                        placeholder="e.g. 10000"
                      />
                      {errors.averageViews && (
                        <p className="mt-1 text-xs text-red-500">{errors.averageViews.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Clients & Awards */}
                <div className="bg-white p-6 rounded-xl shadow">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label htmlFor="clients" className="block text-lg font-semibold text-gray-800 mb-3">
                        Clients
                      </label>
                      <input
                        id="clients"
                        {...register('clients')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        placeholder="Separate with commas (Nike, Adidas, Apple)"
                      />
                    </div>
                    <div>
                      <label htmlFor="awards" className="block text-lg font-semibold text-gray-800 mb-3">
                        Awards
                      </label>
                      <input
                        id="awards"
                        {...register('awards')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        placeholder="Separate with commas (Best Creator 2023, Top 100)"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setEditMode(false);
                      setProfileImage(null);
                      setPreviewImage(getImageUrl(influencerData?.influencerImage));
                    }}
                    className="px-4 py-2 sm:px-6 sm:py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!isDirty || loading}
                    className={`px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl shadow hover:from-blue-700 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors text-sm sm:text-base ${
                      (!isDirty || loading) ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InfluencerProfile;